import React from 'react';
import { ArrowLeft, LayoutGrid, Scroll, Languages } from 'lucide-react';
import { Button, Progress } from '@heroui/react';
import { TransformedGuideData } from '@/types';
import { ViewModeType } from '@/types';

interface GuideViewerHeaderProps {
  guideData: TransformedGuideData;
  viewMode: ViewModeType;
  onViewModeChange: (mode: ViewModeType) => void;
  onBack: () => void;
  onLanguageSwitch: () => void;
  hasOppositeLanguage: boolean;
  oppositeLanguage: string;
  progressPercentage: number;
  timeRemaining: string;
}

const GuideViewerHeader: React.FC<GuideViewerHeaderProps> = ({
  guideData,
  viewMode,
  onViewModeChange,
  onBack,
  onLanguageSwitch,
  hasOppositeLanguage,
  oppositeLanguage,
  progressPercentage,
  timeRemaining,
}) => {
  return (
    <header className="bg-content1 border-b border-divider sticky top-0">
      <div className="mx-auto px-5 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Button isIconOnly variant="light" onPress={onBack} aria-label="Back to guides">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-semibold text-foreground truncate">{guideData.title}</h1>
              {guideData.description && <p className="text-sm text-foreground-500 truncate">{guideData.description}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-content2 rounded-lg p-1">
              <Button
                isIconOnly
                size="sm"
                variant={viewMode === ViewModeType.SLIDE ? 'solid' : 'light'}
                color={viewMode === ViewModeType.SLIDE ? 'primary' : 'default'}
                onPress={() => onViewModeChange(ViewModeType.SLIDE)}
                aria-label="Slide view"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                isIconOnly
                size="sm"
                variant={viewMode === 'scroll' ? 'solid' : 'light'}
                color={viewMode === 'scroll' ? 'primary' : 'default'}
                onPress={() => onViewModeChange(ViewModeType.SCROLL)}
                aria-label="Scroll view"
              >
                <Scroll className="w-4 h-4" />
              </Button>
            </div>

            {/* Language Switch Button */}
            {hasOppositeLanguage && (
              <Button
                size="sm"
                variant="flat"
                startContent={<Languages className="w-4 h-4" />}
                onPress={onLanguageSwitch}
              >
                View {oppositeLanguage === 'es' ? 'Spanish' : 'English'} Version
              </Button>
            )}

            {/* Progress Info */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-foreground-500">Time Remaining</div>
                <div className="text-sm font-semibold text-foreground">{timeRemaining}</div>
              </div>
              <div className="w-32">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-foreground-500">Progress</span>
                  <span className="text-xs font-medium text-foreground">{progressPercentage}%</span>
                </div>
                <Progress value={progressPercentage} color="primary" size="sm" aria-label="Guide progress" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default GuideViewerHeader;
